import './Header.css';
import React, { useState, useEffect, useContext } from "react";
import { Formik } from "formik";
import { Form, Button, Alert, Col, Row } from 'react-bootstrap'
import modelContext from '../modelContext';
import { showFormContext } from '../App';
import initialState from '../Forms/formInitialState'
import { validate_xml, validate_json } from '../xml_validate'
import FileSaver from 'file-saver';
import xmlParser from 'xml-js';
import { Cell4DModel } from './header_types';

interface HeaderProps {
	header: string
	description: string
}

// XML formatter from https://gist.github.com/sente/1083506/d2834134cd070dbcc08bf42ee27dabb746a1c54d#gistcomment-3068708
function formatXml(xml: string): string {
	const PADDING = ' '.repeat(4); // set desired indent size here
	const reg = /(>)(<)(\/*)/g;
	let pad = 0;

	xml = xml.replace(reg, '$1\r\n$2$3');

	return xml.split('\r\n').map((node, index) => {
		let indent = 0;
		if (node.match(/.+<\/\w[^>]*>$/)) {
			indent = 0;
		} else if (node.match(/^<\/\w/) && pad > 0) {
			pad -= 1;
		} else if (node.match(/^<\w[^>]*[^\/]>.*$/)) {
			indent = 1;
		} else {
			indent = 0;
		}

		pad += indent;

		return PADDING.repeat(pad - indent) + node;
	}).join('\r\n');
}

export default function Header(props: HeaderProps) {
	const model = useContext(modelContext);
	const { showForm, changeFormDisplay } = useContext(showFormContext);
	// const { isUploaded, changeUploadStatus } = useContext(handleUpload);
	const [showUpload, changeShowUpload] = useState(false);
	const [uploadError, setUploadError] = useState(false);
	const [uploadErrorMsg, setUploadErrorMsg] = useState({});
	const [downloadError, setDownloadError] = useState(false);
	const [downloadErrorMsg, setDownloadErrorMsg] = useState({});

	let fileReader = new FileReader();

	// takes the input URL and reads it as a text string
	// TypeError from fetch URL can occur if CORS is violated. Ensure proxy is set with package.json, or deal with this with another method appropriately
	async function getTextFromURL(inputURL: string) {
		try {
			console.log("inputURL:", inputURL);
			//const response = await fetch(inputURL, {mode: "cors"});
			const response = await fetch(inputURL);
			const text = await response.text();
			if (!response.ok) {
				throw new Error('Network response was not ok');
			}
			return text;
		} catch (error) {
			console.error('There has been a problem with your fetch operation:', error);
		}
	}

	async function asyncURLFetch(inputURL: string) {

		let result = await getTextFromURL(inputURL)
		return (result)
	}

	// handleSubmit function
	// file uploaded is handled two ways: manual upload using file input with FileReader, or dropdown select from preset models
	// fromURL flag shows what source the input file is coming from, and uses the appropriate functions to retrieve the input
	// file as string, to be converted into XML then JSON with handleInputFileString()
	const submitUpload = (values: any) => {
		if (showForm) {
			let confirmed = window.confirm("Loading a new model file will overwrite all currently existing data from this page." +
				"\r\n Are you sure you would like to continue?");
			if (!confirmed) return;
		}

		values.isLoading = true;

		// if it's from a URL, 
		if (values.fromURL) {
			asyncURLFetch(values.selectFile).then((file_string) => {
				handleInputFileString(file_string);
			});
		}
		else {
			fileReader.onload = handleFileReader;
			fileReader.readAsText(values.uploadFile);
		}
		// changeShowUpload(true);
		values.isLoading = false;
		// changeUploadStatus(true);
	};

	const handleFileReader = () => {
		const content = fileReader.result as string;
		handleInputFileString(content);
	}

	// if the input file is validated, overwrite each section of the saved model object with the corresponding input sections
	// after that, flip the overwriteModel flag. This triggers the useEffect of each tab, overwriting the forms with the new object info
	const handleInputFileString = (content: any) => {
		let validation_result = validate_xml(content, true);
		if (validation_result.pass) {
			setUploadError(false)
		} else {
			setUploadError(true)
			setUploadErrorMsg(validation_result.errors)
			return;
		}

		changeFormDisplay(true);

		// create a copy to modify
		// this way, the original initial state is always maintained
		let initialStateCopy = JSON.parse(JSON.stringify(initialState));

		//breakdown xml object into 6 form sections
		initialStateCopy["cell4d:environmentVariables"] = validation_result.json["sbml"]["model"]["annotation"]["cell4d:environmentVariables"];

		// take care of the background compartment. look for id (index) = 999, if it doesnt exist then make it
		if (validation_result.json?.["sbml"]?.["model"]?.["listOfCompartments"]) {
			let found = false;
			for (let compartIndex = 0; compartIndex < validation_result.json["sbml"]["model"]["listOfCompartments"]["compartment"].length; compartIndex++) {
				let nameAttr = validation_result.json["sbml"]["model"]["listOfCompartments"]["compartment"][compartIndex]._attributes.name;
				if (nameAttr == "999") {
					found = true;
					break;
				}
				// replace name field with index if it isn't already an index
				if(nameAttr.replace(/\D/g, "") !== nameAttr) {
					let paddedIndex = String(compartIndex + 1).padStart(3, "0");
					validation_result.json["sbml"]["model"]["listOfCompartments"]["compartment"][compartIndex]._attributes.name = paddedIndex;
				}
			}
			if(!found) {
				validation_result.json["sbml"]["model"]["listOfCompartments"]["compartment"] = [...validation_result.json["sbml"]["model"]["listOfCompartments"]["compartment"], initialState.listOfCompartments.compartment[0]]
			}
			initialStateCopy["listOfCompartments"] = validation_result.json["sbml"]["model"]["listOfCompartments"];
		} else {
			initialStateCopy["listOfCompartments"] = { compartment: [] };
		}

		if (validation_result.json?.["sbml"]?.["model"]?.["annotation"]?.["cell4d:listOfAnnotationSpeciesTypes"]) {
			initialStateCopy["annotSpecies"] = validation_result.json["sbml"]["model"]["annotation"]["cell4d:listOfAnnotationSpeciesTypes"];
		} else {
			initialStateCopy["annotSpecies"] = { "cell4d:speciesType": [] };
		}

		if (validation_result.json?.["sbml"]?.["model"]?.["listOfSpecies"]) {
			initialStateCopy["listOfSpecies"] = validation_result.json["sbml"]["model"]["listOfSpecies"];
		} else {
			initialStateCopy["listOfSpecies"] = { species: [] };
		}

		// modify list of reactions so that the reactants, products, and modifier all have at least an empty binding site array
		// this is later cleaned up at file output by cleanEmptyBindSites()
		if (validation_result.json?.["sbml"]?.["model"]?.["listOfReactions"]?.reaction) {
			initialStateCopy["listOfReactions"] = validation_result.json["sbml"]["model"]["listOfReactions"];

			initialStateCopy["listOfReactions"].reaction.forEach((reaction: any, reaction_index: number) => {
				reaction.listOfReactants.annotation["cell4d:speciesReference"].forEach((reactant: any, reactant_index: number) => {
					reactant["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"].forEach((molecule: any, molecule_index: number) => {
						let temp_binding_site = molecule?.["cell4d:bindingSite"] ? molecule["cell4d:bindingSite"] : [];
						initialStateCopy["listOfReactions"].reaction[reaction_index].listOfReactants.annotation["cell4d:speciesReference"][reactant_index]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][molecule_index]["cell4d:bindingSite"] =
							temp_binding_site;
					})
				})
				reaction.listOfProducts.annotation["cell4d:speciesReference"].forEach((reactant: any, product_index: number) => {
					reactant["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"].forEach((molecule: any, molecule_index: number) => {
						let temp_binding_site = molecule?.["cell4d:bindingSite"] ? molecule["cell4d:bindingSite"] : [];
						initialStateCopy["listOfReactions"].reaction[reaction_index].listOfProducts.annotation["cell4d:speciesReference"][product_index]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][molecule_index]["cell4d:bindingSite"] =
							temp_binding_site;
					})
				})
				// only add binding site to the molecule if it is an enzymatic reaction with non-empty modifier molecules
				if (reaction?.annotation?.["cell4d:reactionType"]?._text === "Enzymatic") {
					reaction.listOfModifiers.annotation["cell4d:speciesReference"].forEach((reactant: any, product_index: number) => {
						reactant["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"].forEach((molecule: any, molecule_index: number) => {
							let temp_binding_site = molecule?.["cell4d:bindingSite"] ? molecule["cell4d:bindingSite"] : [];
							initialStateCopy["listOfReactions"].reaction[reaction_index].listOfModifiers.annotation["cell4d:speciesReference"][product_index]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][molecule_index]["cell4d:bindingSite"] =
								temp_binding_site;
						})
					})
				}
			})
		} else {
			initialStateCopy["listOfReactions"] = { reaction: [] };
		}

		if (validation_result.json?.["sbml"]?.["model"]?.["annotation"]?.["cell4d:events"]?.["event"]) {
			initialStateCopy["listOfEvents"] = validation_result.json["sbml"]["model"]["annotation"]["cell4d:events"];
		} else {
			initialStateCopy["listOfEvents"] = { event: [] }
		}

		// now that the initialState is replaced by loaded file, set internal model to new initialState
		model.setModelJson(initialStateCopy);
		// reset error status tracking of all tabs to true when new model is loaded
		model.setModelStatus(Array(6).fill(true));
	}


	// recursively traverses the input object and delete all empty arrays that match the target string. In this case, it is "cell4d:bindingSite"
	// cleans up before the file is converted back to an xml, prevents empty tags
	const cleanEmptyBindSites = (obj: any, key: string) => {
		let list: Array<any> = [];
		if (!obj) return;
		if (obj instanceof Array) {
			for (var i in obj) {
				cleanEmptyBindSites(obj[i], key);
			}
		}
		if ((typeof obj == "object") && (obj !== null)) {
			if (obj?.[key]) {
				obj[key] = obj[key].filter((element: any) => !!element);
				if (obj?.[key] instanceof Array && obj?.[key]?.length === 0) {
					delete obj[key];
				}
			}
			let children = Object.keys(obj);
			if (children.length > 0) {
				// if current obj has an empty binding site array, remove it
				for (let i = 0; i < children.length; i++) {
					cleanEmptyBindSites(obj[children[i]], key);
				}
			}
		}
		return list;
	}

	// 
	const handleSaveFile = (fileName: string) => {
		// create default template to be converted into XML
		// additional cell4d:XMLversion tag to tell the simulator that this is generated by the model maker UI, and has ordered compartments
		let json_template: Cell4DModel = {
			"sbml": {
				"_attributes": {
					"level": "2",
					"version": "1",
					"xmlns": "http://www.sbml.org/sbml/level2",
					"xmlns:xsi": "http://www.w3.org/2001/XMLSchema-instance",
					"xsi:schemaLocation": "http://sbml.org/Special/xml-schemas/sbml-l2v1-schema/sbml.xsd",
					"xmlns:cell4d": "http://www.sbml.org/2017/ns/cell4d"
				},
				"model": {
					"_attributes": {
						"id": fileName
					},
					"annotation": {
						"cell4d:XMLversion": {
							"_text": "2",
						},
						"cell4d:environmentVariables": {},
					},
					"listOfCompartments": {},
				},
			}
		}

		// load in each part of the formik forms to the correct json location
		json_template.sbml.model.annotation["cell4d:environmentVariables"] = model.modelJson["cell4d:environmentVariables"];
		json_template.sbml.model.listOfCompartments = model.modelJson.listOfCompartments;
		if ("cell4d:speciesType" in model?.modelJson?.annotSpecies) {
			if(model.modelJson.annotSpecies["cell4d:speciesType"].length > 0) {
				json_template.sbml.model.annotation["cell4d:listOfAnnotationSpeciesTypes"] = model.modelJson.annotSpecies;
			}
		}
		if("species" in model?.modelJson?.listOfSpecies) {
			if (model.modelJson.listOfSpecies.species.length > 0) {
				json_template.sbml.model.listOfSpecies = model.modelJson.listOfSpecies;
			}
		}
		// if (model.modelJson.listOfReactions.length > 0) {
		if ("listOfReactions" in model?.modelJson) {
				json_template.sbml.model.listOfReactions = model.modelJson.listOfReactions;
		}
		// if (model.modelJson.listOfEvents.length > 0) {
		if ("listOfEvents" in model?.modelJson) {
				json_template.sbml.model.annotation["cell4d:events"] = model.modelJson.listOfEvents;
		}

		// clean out empty binding site arrays as well as removing the undefined sites leftover from wildcard reaction reactant/product/mod site arrays
		cleanEmptyBindSites(json_template.sbml.model.listOfSpecies, "cell4d:bindingSite");
		cleanEmptyBindSites(json_template.sbml.model.listOfReactions, "cell4d:bindingSite");

		// revalidate the current model
		let js_validation_result = validate_json(json_template);
		if (js_validation_result.pass) {
			setDownloadError(false)
		} else {
			setDownloadError(true)
			setDownloadErrorMsg(js_validation_result.errors)
			return;
		}
		let xml_out = xmlParser.js2xml(json_template, {
			compact: true,
		})
		// add the required encoding declaration for the XML
		// workaround here concatenating the strings instead of adding to the json object due to xml-js package oversight restricting this
		let prepend_xml_out = `<?xml version="1.0" encoding="UTF-8"?>` + xml_out;
		var blob = new Blob([formatXml(prepend_xml_out)], { type: "text/plain;charset=utf-8" });
		FileSaver.saveAs(blob, fileName + ".xml");
	}

	// force re-render when a file is uploaded
	useEffect(() => {
	}, [uploadError])

	return (
		<>
			<div className="Top">
				<h1 className="Header">
					{props.header}
				</h1>

				<p style={{ fontSize: 18 }}>
					{props.description}
				</p>
				<Formik
					onSubmit={submitUpload}
					initialValues={{
						uploadFile: "",           //string blob for the XML (when loading from user-supplied upload)
						selectFile: "",           //string blob for the preset XML from dropdown
						isLoading: false,   //flag for... something
						fromURL: false      //flag for denoting origin of preloaded XML model

					}}
				>
					{({
						// declare all of the Formik features here.
						handleChange,
						handleSubmit,
						handleBlur,
						values,
						setFieldValue,
					}) => (
						<Form onSubmit={handleSubmit} noValidate>
							<Form.Row>
								<Col xs={5}>
									<Form.Group>
										<Form.File name="uploadFile" label="Load your existing XML model file"
											onChange={(event: any) => { setFieldValue("uploadFile", event.currentTarget.files[0]); changeShowUpload(true); }} />
										<Button type="submit" variant="light" size="sm"
											disabled={values.isLoading || !values.uploadFile}
											onClick={() => setFieldValue("fromURL", false)}>
											{values.isLoading ? `Loading...` : `Upload`}
										</Button>
									</Form.Group>
								</Col>
								<Col xs={2}>
									<Form.Label>or</Form.Label>
								</Col>
								<Col xs={5}>
									{/* nested row/col elements to shrink dropdown width */}
									<Row>
										<Col xs={10}>
											<Form.Group>
												<Form.Label>Load demo model</Form.Label>
												<Form.Control as="select" custom
													id="dropdown-basic-button"
													size="sm"
													name="selectFile"
													// disabled={showForm}
													value={values.selectFile}
													onBlur={handleBlur}
													onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
														handleChange(e);
													}}
												>
													{!values.selectFile ? <option value="">choose option</option> : null}
													<option value="https://compsysbio.org/cell4d_ui/demo_files/AB_fusion_demo.xml">Fusion Demo</option>
													<option value="https://compsysbio.org/cell4d_ui/demo_files/Cascade.xml">Cascade Demo</option>
													<option value="https://compsysbio.org/cell4d_ui/demo_files/CEACAM_transport.xml">CEACAM1 transport demo</option>
													<option value="https://compsysbio.org/cell4d_ui/demo_files/Glycolysis_V5.xml">Glycolysis demo</option>
													<option value="https://compsysbio.org/cell4d_ui/demo_files/model_channel.xml">Channel demo</option>

													
												</Form.Control>
												{/* new button for loading demos */}
												<Button type="submit" variant="light" size="sm"
													disabled={!values.selectFile}
													onClick={() => setFieldValue("fromURL", true)}>
													Load Demo
													</Button>
											</Form.Group>
										</Col>
									</Row>

								</Col>


							</Form.Row>
							<>
								{uploadError ? <Alert variant="danger">File upload error.</Alert> : null}
								{uploadError ? <Alert variant="danger"><pre style={{ textAlign: 'left' }}>{JSON.stringify(uploadErrorMsg, null, 2)}</pre></Alert> : null}
							</>
							<Form.Group style={{ display: 'flex', justifyContent: 'center' }}>
								{!showForm ?
									<Button type="button" variant="light" size="sm" onClick={() => {
										changeFormDisplay(true);
										setUploadError(false);
									}}>Create new form</Button>
									:
									<>
										<Button type="button" variant="light" disabled={!model.modelStatus.every(tab => tab === true)} size="sm" onClick={() => handleSaveFile("model")}>Save model</Button>
										{downloadError ?
											<Row><Alert variant="danger">{JSON.stringify(downloadErrorMsg)}</Alert></Row>
											: null}
									</>
								}
							</Form.Group>
						</Form>
					)}
				</Formik>

			</div>
		</>
	)
}
